import React, { useEffect, useRef, useState } from "react";
import { useInvisible } from "../context/SocketContext";
import { useTitlesHidden } from "../context/MetaContext";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageCircle, User, Users, Lock, UserPlus, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { presenceMeta } from "../lib/presence";
import { nameColorClass } from "../lib/cosmetics";
import { Avatar, profilePath } from "./Avatar";
import { RankBadge } from "./RankBadge";
import { VerifiedBadge } from "./VerifiedBadge";

const cache = new Map();
const W = 300;

function place(rect) {
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - W - 8);
  const below = rect.bottom + 230 < window.innerHeight;
  return below ? { left, top: rect.bottom + 8 } : { left, bottom: window.innerHeight - rect.top + 8 };
}

function MateButton({ card, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const rel = card.relationship;
  if (card.is_self || card.is_mystic || rel === "mates") return null;
  if (rel === "requested") {
    return (
      <button disabled data-testid="hover-card-requested-btn" className="w-full flex items-center justify-center gap-1.5 rounded-full border border-slate-700 bg-slate-800/60 text-slate-400 text-sm font-medium py-1.5 cursor-not-allowed">
        <Clock className="w-4 h-4" /> Requested
      </button>
    );
  }
  const incoming = rel === "incoming";
  const act = async () => {
    setBusy(true);
    try {
      if (incoming) { await api.post(`/mates/accept/${card.friendship_id}`); toast.success(`You and ${card.name} are now pack mates`); }
      else { await api.post(`/mates/request/${card.id}`); toast.success(`Pack Union request sent to ${card.name}`); }
      onUpdate({ ...card, relationship: incoming ? "mates" : "requested", friendship_id: null, mates_count: incoming && card.mates_count != null ? card.mates_count + 1 : card.mates_count });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send that request.");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={act} disabled={busy} data-testid={incoming ? "hover-card-accept-btn" : "hover-card-add-mate-btn"} className="w-full flex items-center justify-center gap-1.5 rounded-full bg-emerald-500/90 hover:bg-emerald-400 disabled:opacity-60 text-slate-950 text-sm font-semibold py-1.5 transition-colors">
      {incoming ? <Check className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />} {incoming ? "Accept" : "Add mate"}
    </button>
  );
}

function CardBody({ card, onGo, onUpdate }) {
  const inv = useInvisible();
  const titlesHidden = useTitlesHidden();
  const pm = presenceMeta(inv.invisible && card.id === inv.meId ? "offline" : card.presence);
  const packLine = card.mates_count == null ? null : `${card.mates_count} mate${card.mates_count === 1 ? "" : "s"}${card.mutual_count ? ` · ${card.mutual_count} mutual` : ""}`;
  return (
    <>
      <div className="flex items-start gap-3">
        <Avatar user={card} size="md" link={false} showPresence presence={card.presence} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 font-semibold truncate" data-testid="hover-card-name">
            <span className={`truncate ${nameColorClass(card.equipped_name_color) || "text-slate-100"}`}>{card.name}</span>
            <VerifiedBadge verified={card.verified} className="w-3.5 h-3.5 shrink-0" />
          </div>
          <div className="text-xs text-slate-500 truncate">@{card.username}</div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <RankBadge rank={card.rank} className="scale-90 origin-left" data-testid="hover-card-rank" />
            {card.title && !titlesHidden && <span className="text-[11px] text-amber-200/90 truncate" data-testid="hover-card-title">{card.title}</span>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
        <span className="flex items-center gap-1.5" data-testid="hover-card-presence"><span className={`w-2 h-2 rounded-full ${pm.color}`} />{pm.label}</span>
        {packLine && <span className="flex items-center gap-1" data-testid="hover-card-pack"><Users className="w-3.5 h-3.5" />{packLine}</span>}
        {card.locked && <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Private den</span>}
      </div>
      {card.bio && <p className="mt-2 text-sm text-slate-300 line-clamp-2" data-testid="hover-card-bio">{card.bio}</p>}
      <div className="mt-3"><MateButton card={card} onUpdate={onUpdate} /></div>
      <div className="mt-2 flex gap-2">
        {!card.is_self && (
          <button onClick={() => onGo(`/messages?to=${card.id}`)} data-testid="hover-card-message-btn" className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 text-sm font-semibold py-1.5 transition-colors">
            <MessageCircle className="w-4 h-4" /> Message
          </button>
        )}
        <button onClick={() => onGo(profilePath(card))} data-testid="hover-card-view-profile-btn" className="flex-1 flex items-center justify-center gap-1.5 rounded-full border border-slate-700 hover:bg-slate-800 text-slate-200 text-sm font-medium py-1.5 transition-colors">
          <User className="w-4 h-4" /> View profile
        </button>
      </div>
    </>
  );
}

export function ProfileHoverCardHost() {
  const [state, setState] = useState(null);
  const [card, setCard] = useState(null);
  const timers = useRef({});
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const clear = () => { clearTimeout(timers.current.show); clearTimeout(timers.current.hide); };
    const onShow = (e) => {
      clear();
      const { user, rect } = e.detail;
      timers.current.show = setTimeout(() => {
        setState({ username: user.username, pos: place(rect) });
        const hit = cache.get(user.username);
        if (hit && hit.at > Date.now() - 30000) { setCard(hit.data); return; }
        setCard(null);
        api.get(`/users/${encodeURIComponent(user.username)}/card`)
          .then((r) => { cache.set(user.username, { at: Date.now(), data: r.data }); setCard(r.data); })
          .catch(() => setState(null));
      }, 450);
    };
    const onHide = () => { clearTimeout(timers.current.show); timers.current.hide = setTimeout(() => setState(null), 220); };
    window.addEventListener("phc:show", onShow);
    window.addEventListener("phc:hide", onHide);
    window.addEventListener("scroll", onHide, true);
    return () => { clear(); window.removeEventListener("phc:show", onShow); window.removeEventListener("phc:hide", onHide); window.removeEventListener("scroll", onHide, true); };
  }, []);

  useEffect(() => { setState(null); }, [pathname]);

  if (!state) return null;
  const go = (to) => { setState(null); navigate(to); };
  const update = (c) => { cache.set(c.username, { at: Date.now(), data: c }); setCard(c); };
  return createPortal(
    <div
      data-testid="profile-hover-card"
      onMouseEnter={() => clearTimeout(timers.current.hide)}
      onMouseLeave={() => { timers.current.hide = setTimeout(() => setState(null), 220); }}
      style={{ ...state.pos, width: W, backgroundColor: "#131B26" }}
      className="fixed z-[200] rounded-2xl border border-slate-700/80 shadow-2xl shadow-black/60 p-4 animate-slide-up"
    >
      {card && card.username === state.username ? <CardBody card={card} onGo={go} onUpdate={update} /> : (
        <div className="flex items-center gap-3" data-testid="hover-card-loading">
          <div className="w-12 h-12 rounded-full bg-slate-800 animate-pulse" />
          <div className="flex-1 space-y-2"><div className="h-3 w-2/3 rounded bg-slate-800 animate-pulse" /><div className="h-3 w-1/3 rounded bg-slate-800 animate-pulse" /></div>
        </div>
      )}
    </div>,
    document.body,
  );
}
